/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Checkbox,
  Divider,
  Flex,
  Link,
  Switch,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import isEqual from 'lodash/isEqual';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FormLabel } from 'src/components/Common/FormComponent';
import { Info } from 'src/components/Common/Info';

import {
  storeSelector,
  templateSelector,
  usePolicyContext,
} from '@Policy/store';
import { useRuleStyle } from './style';
import { useWhyDidYouUpdate } from 'src/utils/hooks';
import { PolicyModeEnum } from '@Policy/utils/constants';
import { ExchangeOnlinePolicyItem } from '@Policy/utils/types';
import {
  ddaApplyBoundDisabledSelector,
  ddaTempQuarantineEnabledSelector,
} from '@Policy/store/filters/virtualAnalyzer';
import ApplyBound from '../../Components/ApplyBound';
import {
  CAS_DOC_LINK,
  getCasOLHLink,
} from '../../../../../../utils/casDocLink';
import i18next from 'i18next';
import { isInUIC } from '@Utils/common';
import CSpinner from '@Components/CSpinner';
import AdvancedTooltipModal from 'src/pages/Dashboard/AdvanceModal';
import { AdvancedFeatureTypeEnum, AdvancedFeatureInfo } from '@Types/app';
import { advancedStyle } from '@Dashboard/AdvanceModal/advancedTooltip';
import { useGlobalSettingStore } from '@Models/useGlobalSettingModel';

export const VaStatus = ({
  monitorModeShow = true,
}: {
  monitorModeShow?: boolean;
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state from store
  const ddaEnabled = policySelector((s) => s.tmDdafilters.ddaEnabled);
  const policyMode = policySelector((s) => s.policyMode);
  const ddaURLEnabled = policySelector((s) => s.tmDdafilters.ddaURLEnabled);
  const ddaMonitorModeEnabled = policySelector(
    (s) => s.tmDdafilters.ddaMonitorModeEnabled
  );
  // get action from store
  const setDdaState = contextSelector((s) => s.setDdaState);
  const checkAdvancedFeature = contextSelector((s) => s.checkAdvancedFeature);
  const { iconColor } = useRuleStyle();
  // local state
  const [switchLoading, setSwitchLoading] = React.useState<boolean>(false);
  const [isAdvancedModalVisible, setIsAdvancedModalVisible] =
    React.useState<boolean>(false);
  const [advancedData, setAdvancedData] = React.useState<AdvancedFeatureInfo>({
    needConfirm: false,
  });
  const switchPolicyEnabled = React.useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked ? 1 : 0;
      if (isInUIC() && e.target.checked && ddaURLEnabled) {
        setSwitchLoading(true);
        const data = await checkAdvancedFeature();
        if (data?.needConfirm) {
          // show confirm dialog
          setAdvancedData(data);
          setIsAdvancedModalVisible(true);
        } else {
          setDdaState({ ddaEnabled: checked, ddaFileEnabled: checked });
        }
        setSwitchLoading(false);
      } else {
        setDdaState({ ddaEnabled: checked, ddaFileEnabled: checked });
      }
    },
    [ddaURLEnabled]
  );
  const handleConfirmAdvancedModal = React.useCallback(() => {
    const checked = 1;
    setDdaState({ ddaEnabled: checked, ddaFileEnabled: checked });
    setIsAdvancedModalVisible(false);
    window.showNotification({
      titleMsg: t('dashboard.serviceStatus.enableAdvancedProtection.success'),
      severity: 'success',
    });
  }, []);
  const handleCloseAdvancedModal = React.useCallback(() => {
    setIsAdvancedModalVisible(false);
  }, []);
  return (
    <>
      <Flex alignItems="center" pt="8px" pb="16px">
        {switchLoading && <CSpinner />}
        <Switch
          mr="1x"
          checked={!!ddaEnabled}
          value={ddaEnabled}
          onChange={switchPolicyEnabled}
        />
        <Text mr="8px" fontSize="18px" lineHeight="24px">
          {t('policy.enableVirtualAnalyzer')}
        </Text>
      </Flex>
      {monitorModeShow ? (
        <Flex alignItems="center" mb="22px">
          <Checkbox
            checked={!!ddaMonitorModeEnabled}
            value={ddaMonitorModeEnabled}
            color={colorStyle.color.secondary}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setDdaState({
                ddaMonitorModeEnabled: e.target.checked ? 1 : 0,
              });
            }}
            disabled={policyMode === PolicyModeEnum.DefaultMonitorPolicy}
          >
            {t('policy.enableVirtualAnalyzer.checkBox')}
          </Checkbox>
          <Info
            iconStyle={{ color: iconColor, ml: '2x' }}
            content={t('policy.enableVirtualAnalyzer.desc')}
          />
        </Flex>
      ) : null}
      <AdvancedTooltipModal
        // isTrialUser={false}
        isVisible={isAdvancedModalVisible}
        onClose={handleCloseAdvancedModal}
        onConfirm={handleConfirmAdvancedModal}
        advanceData={advancedData}
        advanceFeatureType={AdvancedFeatureTypeEnum.enableUrlSandbox}
      />
    </>
  );
};
export const VaApplyBound = () => {
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state from store
  const ddaApplyBound = policySelector((s) => s.tmDdafilters.ddaApplyBound);
  const applyBoundDisabled = contextSelector(
    ddaApplyBoundDisabledSelector,
    isEqual
  );
  // get action from store
  const setDdaApplyBound = contextSelector((s) => s.setDdaApplyBound);
  useWhyDidYouUpdate('VaApplyBound', {
    ddaApplyBound,
    applyBoundDisabled,
  });
  return (
    <ApplyBound
      applyBoundValue={ddaApplyBound}
      setApplyBound={setDdaApplyBound}
      applyBoundKey="ddaApplyBound"
      disabled={!!applyBoundDisabled}
    />
  );
};
export const VaFileAndUrl = () => {
  const { t } = useTranslation();
  const {
    selectedOrgGlobalSetting: { urlSandboxFeatureShow },
  } = useGlobalSettingStore();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state from store
  const ddaEnabled = policySelector((s) => s.tmDdafilters.ddaEnabled);
  const ddaFileEnabled = policySelector((s) => s.tmDdafilters.ddaFileEnabled);
  const ddaURLEnabled = policySelector((s) => s.tmDdafilters.ddaURLEnabled);
  const ddaUrlDisabled = policySelector(
    (s) => s.tmWebthreatprotections.wtpEnabled
  );
  // get action from store
  const setDdaState = contextSelector((s) => s.setDdaState);
  const setDdaUrlEnabled = contextSelector((s) => s.setDdaUrlEnabled);
  const checkAdvancedFeature = contextSelector((s) => s.checkAdvancedFeature);
  // local state
  const { iconColor } = useRuleStyle();
  const [urlLoading, setUrlLoading] = React.useState<boolean>(false);
  const [isAdvancedModalVisible, setIsAdvancedModalVisible] =
    React.useState<boolean>(false);
  const [advancedData, setAdvancedData] = React.useState<AdvancedFeatureInfo>({
    needConfirm: false,
  });
  const isInUic = React.useMemo(() => {
    const isInUic = isInUIC();
    return isInUic;
  }, []);
  const switchUrlEnabled = React.useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      setDdaUrlEnabled(e.target.checked ? 1 : 0);
      if (isInUIC() && e.target.checked && ddaEnabled) {
        setUrlLoading(true);
        const data = await checkAdvancedFeature();
        if (data?.needConfirm) {
          // show confirm dialog
          setAdvancedData(data);
          setIsAdvancedModalVisible(true);
        }
        setUrlLoading(false);
      } else {
        setDdaUrlEnabled(e.target.checked ? 1 : 0);
      }
    },
    [ddaEnabled]
  );
  const handleConfirmAdvancedModal = React.useCallback(() => {
    setDdaUrlEnabled(1);
    setIsAdvancedModalVisible(false);
  }, []);
  const handleCloseAdvancedModal = React.useCallback(() => {
    setDdaUrlEnabled(0);
    setIsAdvancedModalVisible(false);
  }, []);
  useWhyDidYouUpdate('VaFileAndUrl', {
    ddaEnabled,
    ddaFileEnabled,
    ddaURLEnabled,
  });
  return (
    <>
      {urlSandboxFeatureShow ? (
        <Box>
          <Flex mt="23px" alignItems="flex-start">
            <FormLabel label={t('policy.rules.analyzeFollow')} width="200px" />
            <Flex direction="column" justify="center">
              <Flex direction="row" alignItems="center" height="32px">
                <Switch
                  mr="1x"
                  checked={!!ddaFileEnabled}
                  value={ddaFileEnabled}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setDdaState({ ddaFileEnabled: e.target.checked ? 1 : 0 });
                  }}
                  size="sm"
                  disabled={ddaEnabled}
                />
                <Text mr="8px" fontSize="16px" lineHeight="22px">
                  {t('policy.rules.analyzeFollow.files')}
                </Text>
              </Flex>
              <Flex direction="row" alignItems="center" height="32px">
                {urlLoading && <CSpinner />}
                <Switch
                  mr="1x"
                  checked={!!ddaURLEnabled}
                  value={ddaURLEnabled}
                  onChange={switchUrlEnabled}
                  size="sm"
                  disabled={!ddaUrlDisabled}
                />
                <Text mr="8px" fontSize="16px" lineHeight="22px">
                  {t('policy.rules.analyzeFollow.urls')}
                </Text>
                {isInUic || import.meta.env.DEV ? (
                  <sup style={{ ...advancedStyle, left: '-0.5em' }}>
                    {t(
                      `dashboard.serviceStatus.enableAdvancedProtection.superscript`
                    )}
                  </sup>
                ) : null}
                <Info
                  iconStyle={{ color: iconColor }}
                  content={t('policy.rules.analyzeFollow.urls.desc')}
                />
              </Flex>
            </Flex>
          </Flex>
          <AdvancedTooltipModal
            // isTrialUser={false}
            isVisible={isAdvancedModalVisible}
            onClose={handleCloseAdvancedModal}
            onConfirm={handleConfirmAdvancedModal}
            advanceData={advancedData}
            advanceFeatureType={AdvancedFeatureTypeEnum.enableUrlSandbox}
          />
        </Box>
      ) : null}
    </>
  );
};
export const VaTempQuarantine = () => {
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state from store
  const ddaTempQuarantineEnabled = contextSelector(
    ddaTempQuarantineEnabledSelector,
    isEqual
  );
  const vaTempQuarantineEnabled = contextSelector(
    (s) => s.config.featureShow.vaTempQuarantineEnabled
  );
  const ddaMonitorModeEnabled = policySelector(
    (s) => s.tmDdafilters.ddaMonitorModeEnabled
  );
  // get action from store
  const setDdaTempQuarantineEnabled = contextSelector(
    (s) => s.setDdaTempQuarantineEnabled
  );
  // local state
  const { textColor } = useRuleStyle();
  useWhyDidYouUpdate('VaTempQuarantine', {
    ddaTempQuarantineEnabled,
    vaTempQuarantineEnabled,
  });
  return (
    <>
      {vaTempQuarantineEnabled ? (
        <Flex mt="16px" alignItems="center">
          <Checkbox
            checked={!!ddaTempQuarantineEnabled}
            value={ddaTempQuarantineEnabled}
            disabled={!!ddaMonitorModeEnabled}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              // setAsEnabledFeedback();
              setDdaTempQuarantineEnabled(e.target.checked ? 1 : 0);
            }}
            color={textColor}
          >
            {t('policy.rules.virtualAnalyzer.checkBox')}
          </Checkbox>
        </Flex>
      ) : null}
    </>
  );
};
export const VaRulesDescription = () => {
  const {
    selectedOrgGlobalSetting: { urlSandboxFeatureShow },
  } = useGlobalSettingStore();
  // local state
  const { textColor } = useRuleStyle();
  useWhyDidYouUpdate('VaRulesDescription', {
    urlSandboxFeatureShow,
  });
  return (
    <Box mt="16px" whiteSpace="pre-wrap" color={textColor} width="1169px">
      {urlSandboxFeatureShow ? (
        <Trans i18nKey="policy.rules.virtualAnalyzer.checkBox.descForUrlSandBox">
          placeholder
          <Link
            href={getCasOLHLink(CAS_DOC_LINK.policy.atp.va, i18next.language)}
            target="_blank"
          >
            placeholder
          </Link>
        </Trans>
      ) : (
        <Trans i18nKey="policy.rules.virtualAnalyzer.checkBox.desc">
          placeholder
          <Link
            href={getCasOLHLink(CAS_DOC_LINK.policy.atp.va, i18next.language)}
            target="_blank"
          >
            placeholder
          </Link>
        </Trans>
      )}
    </Box>
  );
};

function Rules({
  applyBoundShow,
  tempQuarantineShow,
  monitorModeShow = true,
}: {
  applyBoundShow: boolean;
  tempQuarantineShow: boolean;
  monitorModeShow?: boolean;
}) {
  return (
    <div>
      <Box ml="1px" mt="16px">
        <VaStatus monitorModeShow={monitorModeShow} />
        <Divider mt="10px" mb="10px" />
        <VaFileAndUrl />
        {applyBoundShow ? <VaApplyBound /> : null}
        {tempQuarantineShow ? <VaTempQuarantine /> : null}
        <VaRulesDescription />
      </Box>
    </div>
  );
}

export default React.memo(Rules);
